/**
 * The planner on phones: pick a day, then see each person's load and
 * assignments for it. Tapping an assignment edits it; "Plan" adds one.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toDateValue } from "../../components/ui/controls.js";
import { cx } from "../../components/ui/cx.js";
import {
  hours,
  loadClass,
  minutesBetween,
  type PlannerAssignment,
  type PlannerData,
} from "./planner-model.js";

export function PlannerDayList({
  days,
  data,
  byCell,
  holidays,
  locale,
  onCreate,
  onEdit,
}: {
  days: Date[];
  data: PlannerData;
  byCell: {
    assignments: Map<string, PlannerAssignment[]>;
    absences: Map<string, PlannerData["absences"]>;
  };
  holidays: Map<string, string>;
  locale: string;
  onCreate: (projectId: string, userId: string, date: string) => void;
  onEdit: (assignment: PlannerAssignment) => void;
}) {
  const { t } = useTranslation();
  const today = toDateValue(new Date());
  const dates = days.map(toDateValue);
  const [picked, setPicked] = useState<string | null>(null);
  const selected =
    picked && dates.includes(picked)
      ? picked
      : dates.includes(today)
        ? today
        : dates[0]!;
  const day = new Date(`${selected}T12:00:00`);
  const weekday = day.getDay() || 7;
  const holiday = holidays.get(selected);
  const format = (options: Intl.DateTimeFormatOptions, value: Date) =>
    new Intl.DateTimeFormat(locale, options).format(value);

  return (
    <div className="space-y-3">
      <div
        role="tablist"
        aria-label={t("planner.day")}
        className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1"
      >
        {days.map((value) => {
          const date = toDateValue(value);
          const active = date === selected;
          return (
            <button
              key={date}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setPicked(date)}
              className={cx(
                "flex w-12 shrink-0 flex-col items-center rounded-xl border py-1.5 text-xs",
                active
                  ? "border-brand bg-brand text-white"
                  : "border-app-border bg-app-surface text-app-muted",
                !active && date === today && "border-brand text-brand",
              )}
            >
              <span>{format({ weekday: "short" }, value)}</span>
              <span className="text-base font-semibold">
                {format({ day: "numeric" }, value)}
              </span>
              {holidays.has(date) ? (
                <span className="h-1 w-1 rounded-full bg-warning" />
              ) : null}
            </button>
          );
        })}
      </div>
      <p className="text-sm font-medium">
        {format({ dateStyle: "full" }, day)}
        {holiday ? (
          <span className="ml-2 text-xs text-warning">{holiday}</span>
        ) : null}
      </p>
      <ul className="space-y-2">
        {data.resources.map((resource) => {
          const key = `${resource.id}:${selected}`;
          const assignments = byCell.assignments.get(key) ?? [];
          const absences = byCell.absences.get(key) ?? [];
          const capacity = holiday
            ? 0
            : (resource.workSchedules.find(
                (schedule) => schedule.weekday === weekday,
              )?.plannedMinutes ?? 0);
          const booked =
            assignments.reduce(
              (sum, item) => sum + minutesBetween(item.startAt, item.endAt),
              0,
            ) + absences.reduce((sum, item) => sum + item.minutes, 0);
          const name = `${resource.firstName} ${resource.lastName}`;
          return (
            <li
              key={resource.id}
              className="rounded-xl border border-app-border bg-app-surface p-3"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium">{name}</span>
                <span className="text-xs tabular-nums text-app-muted">
                  {hours(booked)} / {hours(capacity)}
                </span>
              </div>
              <span className="mt-1.5 block h-1 overflow-hidden rounded bg-app-background">
                <span
                  className={cx("block h-full", loadClass(booked, capacity))}
                  style={{
                    width: `${Math.min(100, capacity ? (booked / capacity) * 100 : booked ? 100 : 0)}%`,
                  }}
                />
              </span>
              <div className="mt-2 space-y-1.5">
                {absences.map((absence, index) => (
                  <span
                    key={`${absence.kind}-${index}`}
                    className="block rounded-md bg-warning-soft px-2 py-1 text-xs text-warning"
                  >
                    {t(`time.kind.${absence.kind}`)} · {hours(absence.minutes)}
                  </span>
                ))}
                {assignments.map((assignment) => (
                  <button
                    key={assignment.id}
                    type="button"
                    onClick={() => onEdit(assignment)}
                    className="flex w-full items-center justify-between gap-2 rounded-lg border border-info-border bg-info-soft px-2.5 py-2 text-left text-sm text-info"
                  >
                    <span className="min-w-0 truncate font-medium">
                      {assignment.project.title}
                    </span>
                    <span className="shrink-0 text-xs tabular-nums">
                      {assignment.startTime}–{assignment.endTime}
                    </span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => onCreate("", resource.id, selected)}
                  className="w-full rounded-lg border border-dashed border-app-border py-2 text-sm text-app-muted"
                  aria-label={t("planner.assignOn", { name, date: selected })}
                >
                  + {t("planner.assign")}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
